import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { submitMaturity } from '../utils/maturityJobs';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import { Button } from '../components/ui/button';
import { ReportSecurityNote } from '../components/ReportSecurityNote';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import {
  Shield, ShieldCheck, ShieldAlert, Lock, Network, Monitor, AlertTriangle,
  Users, ChevronDown, ChevronUp, FileText, Target, Clock, ArrowRight,
  TrendingUp, TrendingDown, AlertCircle, CheckCircle2, Info, Home,
  Building2, Mail, User, Briefcase, ChevronRight, ChevronLeft, Loader2,
  Download, KeyRound, Eye, EyeOff, Brain
} from 'lucide-react';
import { generateCyberSecurityPptx } from '../utils/generateCyberSecurityPptx';
import AGIInsightsTab from '../components/agi/AGIInsightsTab';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Domain & Control Definitions ───
const DOMAINS = [
  {
    id: 'governance',
    name: 'Governance & Risk',
    subtitle: 'Policies, risk management, regulatory compliance',
    icon: Shield,
    color: '#2563eb',
    controls: [
      { id: 'gov_1', name: 'Information security policy exists and is reviewed annually' },
      { id: 'gov_2', name: 'Risk register maintained with regular review cycle' },
      { id: 'gov_3', name: 'Regulatory compliance (GDPR, NIS2, ISO 27001) mapped' },
      { id: 'gov_4', name: 'Executive / board-level cybersecurity ownership' },
      { id: 'gov_5', name: 'Third-party / supply chain risk process in place' },
    ],
  },
  {
    id: 'iam',
    name: 'Identity & Access Management',
    subtitle: 'IAM, MFA, privilege management',
    icon: Lock,
    color: '#059669',
    controls: [
      { id: 'iam_1', name: 'Multi-factor authentication enforced for all users' },
      { id: 'iam_2', name: 'Privileged access management (PAM) solution deployed' },
      { id: 'iam_3', name: 'Joiners/movers/leavers process is automated' },
      { id: 'iam_4', name: 'Least-privilege principle enforced across systems' },
      { id: 'iam_5', name: 'Regular access reviews and certification campaigns' },
    ],
  },
  {
    id: 'network',
    name: 'Network & Infrastructure',
    subtitle: 'Network segmentation, perimeter, cloud posture',
    icon: Network,
    color: '#dc2626',
    controls: [
      { id: 'net_1', name: 'Network segmentation and micro-segmentation applied' },
      { id: 'net_2', name: 'Firewall and IDS/IPS actively managed and reviewed' },
      { id: 'net_3', name: 'Cloud security posture management (CSPM) in place' },
      { id: 'net_4', name: 'Vulnerability scanning run on all assets (weekly+)' },
      { id: 'net_5', name: 'Patch management SLAs defined and met' },
    ],
  },
  {
    id: 'endpoint',
    name: 'Endpoint & Detection',
    subtitle: 'EDR, SIEM, threat detection capability',
    icon: Monitor,
    color: '#d97706',
    controls: [
      { id: 'edr_1', name: 'Endpoint detection & response (EDR) on all endpoints' },
      { id: 'edr_2', name: 'Security information & event management (SIEM) active' },
      { id: 'edr_3', name: '24/7 security operations centre (SOC) or MDR service' },
      { id: 'edr_4', name: 'Threat intelligence feeds integrated into detections' },
      { id: 'edr_5', name: 'Mean time to detect (MTTD) tracked and improving' },
    ],
  },
  {
    id: 'incident',
    name: 'Incident Response',
    subtitle: 'IR plan, playbooks, business continuity',
    icon: AlertTriangle,
    color: '#7c3aed',
    controls: [
      { id: 'ir_1', name: 'Documented IR plan tested in last 12 months' },
      { id: 'ir_2', name: 'Playbooks exist for top 5 threat scenarios' },
      { id: 'ir_3', name: 'Business continuity / disaster recovery plan tested' },
      { id: 'ir_4', name: 'Legal, comms & PR response process defined' },
      { id: 'ir_5', name: 'Post-incident review process with lessons learned' },
    ],
  },
  {
    id: 'awareness',
    name: 'Awareness & Culture',
    subtitle: 'Training, phishing simulation, culture metrics',
    icon: Users,
    color: '#0891b2',
    controls: [
      { id: 'aw_1', name: 'Annual mandatory security awareness training completed' },
      { id: 'aw_2', name: 'Phishing simulation campaigns run quarterly' },
      { id: 'aw_3', name: 'Security champion programme active in key teams' },
      { id: 'aw_4', name: 'Metrics tracked for training completion and click rates' },
      { id: 'aw_5', name: 'Security embedded into software development lifecycle' },
    ],
  },
];

const SECTORS = [
  'Aerospace & Defense', 'Agriculture', 'Automotive', 'Biotechnology', 'Chemicals',
  'Consumer Packaged Goods', 'Education', 'Energy', 'EPC (Engineering, Procurement & Construction)', 'Financial Services',
  'Food Processing', 'Healthcare', 'Insurance', 'Manufacturing',
  'Media & Entertainment', 'Oil & Gas', 'Pharmaceuticals', 'Professional Services',
  'Real Estate & Construction', 'Retail', 'Software', 'Technology',
  'Telecommunications', 'Travel & Logistics', 'Utilities',
];

const ORG_SIZES = [
  { value: '1-50', label: '1-50 employees' },
  { value: '51-250', label: '51-250 employees' },
  { value: '251-1000', label: '251-1,000 employees' },
  { value: '1001-5000', label: '1,001-5,000 employees' },
  { value: '5000+', label: '5,000+ employees' },
];

const REVENUE_RANGES = [
  { value: '$0-1M', label: 'Under $1 Million' },
  { value: '$1M-10M', label: '$1M - $10 Million' },
  { value: '$10M-100M', label: '$10M - $100 Million' },
  { value: '$100M-1B', label: '$100M - $1 Billion' },
  { value: '$1B+', label: 'Over $1 Billion' },
];

const MATURITY_LABELS = { 1: 'Initial', 2: 'Developing', 3: 'Defined', 4: 'Managed', 5: 'Optimised' };
const MATURITY_COLORS = { 1: '#ef4444', 2: '#f97316', 3: '#eab308', 4: '#22c55e', 5: '#0ea5e9' };

// ─── Helpers ───
function getOverallMaturity(scores) {
  const all = Object.values(scores);
  return all.length ? +(all.reduce((a, b) => a + b, 0) / all.length).toFixed(1) : 0;
}
function getDomainScore(domain, scores) {
  const vals = domain.controls.map(c => scores[c.id] || 1);
  return +(vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
}
function getMaturityLabel(s) { return MATURITY_LABELS[Math.round(s)] || MATURITY_LABELS[1]; }
function getScoreColor(s) {
  if (s >= 4.5) return '#0ea5e9';
  if (s >= 3.5) return '#22c55e';
  if (s >= 2.5) return '#eab308';
  if (s >= 1.5) return '#f97316';
  return '#ef4444';
}

// ─── Step: Assessment Sliders (one domain per step) ───
const DomainStep = ({ domain, scores, setScores }) => {
  const Icon = domain.icon;
  const domainScore = getDomainScore(domain, scores);

  return (
    <div className="bg-white rounded-2xl shadow-lg p-8">
      <div className="flex items-center gap-4 mb-6 pb-5 border-b border-gray-100">
        <div className="p-3 rounded-xl" style={{ backgroundColor: `${domain.color}15` }}>
          <Icon className="w-7 h-7" style={{ color: domain.color }} />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-gray-900">{domain.name}</h2>
          <p className="text-sm text-gray-500">{domain.subtitle}</p>
        </div>
        <Badge className="text-white text-sm px-3 py-1" style={{ backgroundColor: getScoreColor(domainScore) }}>
          {domainScore} / 5
        </Badge>
      </div>

      <div className="space-y-7">
        {domain.controls.map(control => {
          const val = scores[control.id] || 1;
          return (
            <div key={control.id} data-testid={`control-${control.id}`}>
              <div className="flex items-start justify-between mb-2.5">
                <span className="text-sm text-gray-700 pr-4 leading-snug font-medium">{control.name}</span>
                <span className="text-sm font-bold whitespace-nowrap" style={{ color: getScoreColor(val) }}>
                  Level {val}
                </span>
              </div>
              <Slider
                data-testid={`slider-${control.id}`}
                value={[val]}
                min={1} max={5} step={1}
                onValueChange={([v]) => setScores(p => ({ ...p, [control.id]: v }))}
                className="w-full"
              />
              <div className="flex justify-between mt-1.5 text-[10px] text-gray-400">
                {Object.entries(MATURITY_LABELS).map(([k, v]) => (
                  <span key={k} className={val === Number(k) ? 'font-semibold text-gray-600' : ''}>{v}</span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Dashboard Tab ───
const DashboardTab = ({ scores, profile }) => {
  const domainScores = DOMAINS.map(d => ({ domain: d.name.split(' ')[0], fullName: d.name, score: getDomainScore(d, scores), color: d.color }));
  const overall = getOverallMaturity(scores);
  const maxD = domainScores.reduce((a, b) => a.score > b.score ? a : b);
  const minD = domainScores.reduce((a, b) => a.score < b.score ? a : b);
  const gapCount = Object.values(scores).filter(v => v <= 2).length;
  const radarData = domainScores.map(d => ({ subject: d.domain, score: d.score, fullMark: 5 }));

  return (
    <div className="space-y-6">
      {/* Company Context */}
      {profile.company_name && (
        <div className="bg-gradient-to-r from-slate-800 to-emerald-900 rounded-xl p-6 text-white">
          <h2 className="text-xl font-bold">{profile.company_name}</h2>
          <p className="text-gray-300 text-sm">{profile.industry} {profile.org_size ? `| ${ORG_SIZES.find(o => o.value === profile.org_size)?.label}` : ''}</p>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-metrics">
        <MetricCard title="Overall Maturity" value={overall} subtitle={getMaturityLabel(overall)} color={getScoreColor(overall)} icon={ShieldCheck} />
        <MetricCard title="Strongest Domain" value={maxD.score} subtitle={maxD.fullName} color="#22c55e" icon={TrendingUp} />
        <MetricCard title="Weakest Domain" value={minD.score} subtitle={minD.fullName} color="#ef4444" icon={TrendingDown} />
        <MetricCard title="Critical Gaps" value={gapCount} subtitle="of 30 controls at Level 1-2" color={gapCount > 10 ? '#ef4444' : gapCount > 5 ? '#f97316' : '#22c55e'} icon={AlertCircle} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="radar-chart">
          <h3 className="text-base font-semibold text-gray-800 mb-4">Maturity Radar</h3>
          <ResponsiveContainer width="100%" height={320}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="#e5e7eb" />
              <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11, fill: '#6b7280' }} />
              <PolarRadiusAxis angle={90} domain={[0, 5]} tick={{ fontSize: 10, fill: '#9ca3af' }} />
              <Radar dataKey="score" stroke="#059669" fill="#059669" fillOpacity={0.25} strokeWidth={2} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="bar-chart">
          <h3 className="text-base font-semibold text-gray-800 mb-4">Domain Scores</h3>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={domainScores} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis type="number" domain={[0, 5]} tick={{ fontSize: 11, fill: '#9ca3af' }} />
              <YAxis type="category" dataKey="domain" tick={{ fontSize: 11, fill: '#6b7280' }} width={90} />
              <Tooltip formatter={(v) => [`${v} / 5`, 'Score']} />
              <Bar dataKey="score" radius={[0, 6, 6, 0]} barSize={28}>
                {domainScores.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Maturity Scale</h3>
        <div className="flex flex-wrap gap-4">
          {Object.entries(MATURITY_LABELS).map(([level, label]) => (
            <div key={level} className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: MATURITY_COLORS[level] }} />
              <span className="text-xs text-gray-600">Level {level}: {label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const MetricCard = ({ title, value, subtitle, color, icon: Icon }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5">
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</span>
      <Icon className="w-4 h-4" style={{ color }} />
    </div>
    <div className="text-2xl font-bold" style={{ color }}>{value}</div>
    <p className="text-xs text-gray-500 mt-1 truncate">{subtitle}</p>
  </div>
);

// ─── Findings Tab ───
const FindingsTab = ({ scores }) => {
  const findings = useMemo(() => {
    const gaps = [];
    DOMAINS.forEach(domain => {
      domain.controls.forEach(control => {
        const score = scores[control.id] || 1;
        if (score < 4) {
          gaps.push({
            control: control.name, domain: domain.name, domainColor: domain.color,
            currentLevel: score, targetLevel: Math.min(score + 2, 5),
            priority: score <= 1 ? 'Critical' : score <= 2 ? 'High' : 'Medium',
            priorityColor: score <= 1 ? '#ef4444' : score <= 2 ? '#f97316' : '#eab308',
          });
        }
      });
    });
    return gaps.sort((a, b) => a.currentLevel - b.currentLevel);
  }, [scores]);

  const criticalCount = findings.filter(f => f.priority === 'Critical').length;
  const highCount = findings.filter(f => f.priority === 'High').length;
  const mediumCount = findings.filter(f => f.priority === 'Medium').length;

  return (
    <div className="space-y-5" data-testid="findings-tab">
      <div className="flex gap-3 flex-wrap">
        <Badge variant="outline" className="text-red-600 border-red-200 bg-red-50 px-3 py-1">{criticalCount} Critical</Badge>
        <Badge variant="outline" className="text-orange-600 border-orange-200 bg-orange-50 px-3 py-1">{highCount} High</Badge>
        <Badge variant="outline" className="text-yellow-700 border-yellow-200 bg-yellow-50 px-3 py-1">{mediumCount} Medium</Badge>
      </div>
      {findings.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-green-500" />
          <p className="font-medium">No significant gaps found</p>
          <p className="text-sm">All controls are at Level 4 or above</p>
        </div>
      ) : (
        <div className="space-y-3">
          {findings.map((f, i) => (
            <div key={i} data-testid={`finding-${i}`} className="bg-white border border-gray-200 rounded-lg p-4 flex items-start gap-4 hover:shadow-sm transition-shadow">
              <div className="mt-0.5 w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: f.priorityColor }} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800">{f.control}</p>
                <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                  <Badge variant="outline" className="text-xs" style={{ color: f.domainColor, borderColor: `${f.domainColor}40` }}>{f.domain}</Badge>
                  <span className="text-xs text-gray-500">Level {f.currentLevel} <ArrowRight className="w-3 h-3 inline" /> Level {f.targetLevel}</span>
                </div>
              </div>
              <Badge className="text-xs text-white shrink-0" style={{ backgroundColor: f.priorityColor }}>{f.priority}</Badge>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Recommendations Tab ───
const RECOMMENDATION_MAP = {
  gov_1: { immediate: 'Draft information security policy or update existing one', '30day': 'Get policy approved by board and distribute to all staff', '90day': 'Establish annual review cycle with change tracking', ongoing: 'Continuous alignment with evolving regulations' },
  gov_2: { immediate: 'Create a basic risk register spreadsheet', '30day': 'Populate register with top 20 risks and assign owners', '90day': 'Implement quarterly risk review meetings', ongoing: 'Integrate risk register with project and change processes' },
  gov_3: { immediate: 'List all applicable regulations (GDPR, NIS2, etc.)', '30day': 'Map current controls to regulatory requirements', '90day': 'Close top compliance gaps and document evidence', ongoing: 'Maintain compliance dashboard with regular audits' },
  gov_4: { immediate: 'Appoint a board-level security sponsor', '30day': 'Schedule monthly CISO-to-board reporting', '90day': 'Establish security KPIs on board agenda', ongoing: 'Annual board-level security strategy review' },
  gov_5: { immediate: 'Inventory all third-party vendors with data access', '30day': 'Implement security questionnaire for critical vendors', '90day': 'Establish vendor risk tiering and SLA requirements', ongoing: 'Continuous monitoring and annual vendor reassessment' },
  iam_1: { immediate: 'Enable MFA for all admin and privileged accounts', '30day': 'Roll out MFA to all remote access users', '90day': 'Enforce MFA organisation-wide including cloud apps', ongoing: 'Monitor MFA bypass attempts and adopt phishing-resistant MFA' },
  iam_2: { immediate: 'Audit all privileged accounts and remove orphans', '30day': 'Implement PAM solution for critical systems', '90day': 'Enforce just-in-time access for all admin tasks', ongoing: 'Record and review privileged sessions regularly' },
  iam_3: { immediate: 'Document current JML process and identify gaps', '30day': 'Automate account provisioning via HR-IT integration', '90day': 'Implement automated deprovisioning within 24hrs of departure', ongoing: 'Quarterly orphan account sweeps and process reviews' },
  iam_4: { immediate: 'Review and remove unnecessary admin rights', '30day': 'Implement role-based access control (RBAC) model', '90day': 'Enforce least privilege across all systems', ongoing: 'Continuous access analytics and drift detection' },
  iam_5: { immediate: 'Schedule access review for critical applications', '30day': 'Implement quarterly access certification campaigns', '90day': 'Automate access reviews with manager attestation', ongoing: 'Integrate access reviews into governance workflow' },
  net_1: { immediate: 'Map current network architecture and trust zones', '30day': 'Implement basic VLAN segmentation for critical assets', '90day': 'Deploy micro-segmentation for sensitive workloads', ongoing: 'Regular segmentation testing and zero-trust progression' },
  net_2: { immediate: 'Review and update firewall rules, remove unused rules', '30day': 'Implement IDS/IPS with tuned rulesets', '90day': 'Establish quarterly firewall rule review process', ongoing: 'Continuous rule optimisation and threat feed integration' },
  net_3: { immediate: 'Inventory all cloud assets and configurations', '30day': 'Deploy CSPM tool and remediate critical findings', '90day': 'Implement automated policy enforcement', ongoing: 'Continuous cloud posture monitoring and drift alerts' },
  net_4: { immediate: 'Deploy vulnerability scanner across all known assets', '30day': 'Establish weekly scanning cadence and SLA for remediation', '90day': 'Achieve full asset coverage including cloud and containers', ongoing: 'Trend analysis, KPI tracking, and risk-based prioritisation' },
  net_5: { immediate: 'Define patch SLAs (critical: 48h, high: 7d, medium: 30d)', '30day': 'Implement automated patching for endpoints', '90day': 'Achieve 95%+ patch compliance across all systems', ongoing: 'Monitor patch drift and exception management' },
  edr_1: { immediate: 'Deploy EDR agent on all managed endpoints', '30day': 'Tune detection policies and reduce alert noise', '90day': 'Achieve 100% endpoint coverage including servers', ongoing: 'Regular EDR policy review and threat hunting' },
  edr_2: { immediate: 'Centralise log collection from critical systems', '30day': 'Implement SIEM with correlation rules for top threats', '90day': 'Expand log sources and create custom detection logic', ongoing: 'Continuous rule tuning and detection engineering' },
  edr_3: { immediate: 'Evaluate SOC/MDR service options', '30day': 'Implement 24/7 monitoring capability (internal or MDR)', '90day': 'Establish SLAs for alert triage and escalation', ongoing: 'Regular service reviews and threat landscape updates' },
  edr_4: { immediate: 'Subscribe to at least one threat intelligence feed', '30day': 'Integrate feeds into SIEM and EDR platforms', '90day': 'Establish threat intel sharing with industry peers', ongoing: 'Continuous threat landscape monitoring and IOC enrichment' },
  edr_5: { immediate: 'Start tracking MTTD for all security incidents', '30day': 'Set baseline metrics and improvement targets', '90day': 'Reduce MTTD by 30% through process improvements', ongoing: 'Continuous MTTD/MTTR optimisation and benchmarking' },
  ir_1: { immediate: 'Create or update documented incident response plan', '30day': 'Conduct tabletop exercise with key stakeholders', '90day': 'Run full simulation exercise and update plan', ongoing: 'Annual plan review and bi-annual exercises' },
  ir_2: { immediate: 'Identify top 5 threat scenarios for your organisation', '30day': 'Create detailed playbooks for each scenario', '90day': 'Test all playbooks through simulation exercises', ongoing: 'Regular playbook updates based on threat landscape' },
  ir_3: { immediate: 'Review and update BCP/DR documentation', '30day': 'Test backup restoration for critical systems', '90day': 'Conduct full DR failover test', ongoing: 'Annual DR testing and continuous improvement' },
  ir_4: { immediate: 'Identify legal, comms, and PR contacts for incidents', '30day': 'Create communications templates for common scenarios', '90day': 'Conduct integrated response exercise with all parties', ongoing: 'Regular stakeholder alignment and template updates' },
  ir_5: { immediate: 'Establish post-incident review template', '30day': 'Conduct reviews for all recent incidents', '90day': 'Create lessons-learned database and tracking system', ongoing: 'Continuous improvement loop from incident learnings' },
  aw_1: { immediate: 'Schedule mandatory security training for all staff', '30day': 'Roll out interactive training programme organisation-wide', '90day': 'Achieve 95%+ training completion rate', ongoing: 'Annual refresher training with updated content' },
  aw_2: { immediate: 'Plan first phishing simulation campaign', '30day': 'Execute baseline phishing simulation and measure click rate', '90day': 'Establish quarterly simulation cadence', ongoing: 'Track improvement trends and targeted re-training' },
  aw_3: { immediate: 'Identify and recruit security champions in key teams', '30day': 'Provide champion training and define responsibilities', '90day': 'Establish regular champion meetings and knowledge sharing', ongoing: 'Champion programme expansion and recognition' },
  aw_4: { immediate: 'Define key security awareness metrics to track', '30day': 'Implement dashboard for training and simulation metrics', '90day': 'Set improvement targets and report to leadership', ongoing: 'Continuous metric refinement and benchmarking' },
  aw_5: { immediate: 'Add security review gate to SDLC process', '30day': 'Implement SAST/DAST tools in CI/CD pipeline', '90day': 'Train developers on secure coding practices', ongoing: 'Continuous DevSecOps maturity improvement' },
};

const RecommendationsTab = ({ scores }) => {
  const recommendations = useMemo(() => {
    const buckets = { immediate: [], '30day': [], '90day': [], ongoing: [] };
    DOMAINS.forEach(domain => {
      domain.controls.forEach(control => {
        const score = scores[control.id] || 1;
        const rec = RECOMMENDATION_MAP[control.id];
        if (!rec) return;
        if (score <= 2) {
          buckets.immediate.push({ ...rec, control: control.name, domain: domain.name, domainColor: domain.color, score, action: rec.immediate });
          buckets['30day'].push({ ...rec, control: control.name, domain: domain.name, domainColor: domain.color, score, action: rec['30day'] });
        }
        if (score <= 3) {
          buckets['90day'].push({ ...rec, control: control.name, domain: domain.name, domainColor: domain.color, score, action: rec['90day'] });
        }
        buckets.ongoing.push({ ...rec, control: control.name, domain: domain.name, domainColor: domain.color, score, action: rec.ongoing });
      });
    });
    return buckets;
  }, [scores]);

  const timeframes = [
    { key: 'immediate', label: 'Immediate', color: '#ef4444', icon: AlertCircle, desc: 'Address within 1 week' },
    { key: '30day', label: '30-Day Plan', color: '#f97316', icon: Clock, desc: 'Complete within 30 days' },
    { key: '90day', label: '90-Day Plan', color: '#eab308', icon: Target, desc: 'Complete within 90 days' },
    { key: 'ongoing', label: 'Ongoing', color: '#22c55e', icon: TrendingUp, desc: 'Continuous improvement' },
  ];

  return (
    <div className="space-y-6" data-testid="recommendations-tab">
      {timeframes.map(tf => {
        const items = recommendations[tf.key];
        const Icon = tf.icon;
        if (items.length === 0) return null;
        return (
          <div key={tf.key}>
            <div className="flex items-center gap-2 mb-3">
              <Icon className="w-5 h-5" style={{ color: tf.color }} />
              <h3 className="font-semibold text-gray-800">{tf.label}</h3>
              <Badge variant="outline" className="text-xs">{items.length} actions</Badge>
              <span className="text-xs text-gray-400 ml-1">{tf.desc}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {items.slice(0, 8).map((item, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow" style={{ borderLeftWidth: 3, borderLeftColor: tf.color }}>
                  <p className="text-sm text-gray-800 font-medium mb-2">{item.action}</p>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px]" style={{ color: item.domainColor, borderColor: `${item.domainColor}40` }}>{item.domain}</Badge>
                    <span className="text-[10px] text-gray-400">Level {item.score} / 5</span>
                  </div>
                </div>
              ))}
            </div>
            {items.length > 8 && <p className="text-xs text-gray-400 mt-2 ml-1">+ {items.length - 8} more actions</p>}
          </div>
        );
      })}
    </div>
  );
};

// ─── Report Tab ───
const ReportTab = ({ scores, profile }) => {
  const report = useMemo(() => {
    const overall = getOverallMaturity(scores);
    const domainScores = DOMAINS.map(d => ({ ...d, score: getDomainScore(d, scores) }));
    const strongest = domainScores.reduce((a, b) => a.score > b.score ? a : b);
    const weakest = domainScores.reduce((a, b) => a.score < b.score ? a : b);
    const critGaps = Object.entries(scores).filter(([, v]) => v <= 1).length;
    const highGaps = Object.entries(scores).filter(([, v]) => v === 2).length;
    const sectorText = profile.industry ? ` in the ${profile.industry} sector` : '';
    const orgText = profile.org_size ? ` (${ORG_SIZES.find(o => o.value === profile.org_size)?.label || profile.org_size})` : '';
    const companyText = profile.company_name ? ` for ${profile.company_name}` : ' for the organisation';

    return {
      title: 'Cyber Security Maturity Assessment Report',
      summary: `This report presents the results of a comprehensive cybersecurity maturity assessment conducted${companyText}${sectorText}${orgText}. The assessment evaluated 30 security controls across 6 key domains using a 5-level maturity model aligned with industry frameworks including NIST CSF, ISO 27001, and CIS Controls.`,
      overallScore: `The organisation achieved an overall maturity score of ${overall} out of 5.0, placing it at the "${getMaturityLabel(overall)}" maturity level. This indicates that ${
        overall < 2 ? 'fundamental security practices require urgent attention, with significant gaps across multiple domains'
        : overall < 3 ? 'basic security practices are in place but require formalisation and consistent application across the organisation'
        : overall < 4 ? 'security practices are generally well-defined, with opportunities for optimisation and automation'
        : 'the organisation demonstrates a strong security posture with mature processes and continuous improvement practices'
      }.`,
      domainAnalysis: domainScores.map(d => ({
        name: d.name, score: d.score, color: d.color,
        analysis: `${d.name}: ${d.score}/5.0 (${getMaturityLabel(d.score)}). ${
          d.score < 2 ? 'Requires immediate investment and remediation.'
          : d.score < 3 ? 'Basic capabilities exist but need strengthening.'
          : d.score < 4 ? 'Good foundation in place with room for optimisation.'
          : 'Strong maturity with continuous improvement practices.'
        }`,
      })),
      keyFindings: [
        `Strongest domain: ${strongest.name} at ${strongest.score}/5.0`,
        `Weakest domain: ${weakest.name} at ${weakest.score}/5.0`,
        `${critGaps} controls at Critical level (Level 1) requiring immediate attention`,
        `${highGaps} controls at High risk (Level 2) requiring near-term remediation`,
        `${30 - critGaps - highGaps} controls at acceptable maturity (Level 3+)`,
      ],
      recommendation: overall < 2
        ? 'The assessment reveals significant cybersecurity gaps that present material risk to the organisation. An urgent, board-sponsored remediation programme is recommended with dedicated budget allocation.'
        : overall < 3
        ? 'The organisation has a developing security posture but faces notable gaps. A structured 90-day improvement programme focusing on critical and high-priority findings is recommended.'
        : overall < 4
        ? 'The organisation has a solid security foundation. Focus should shift to optimising existing controls, implementing automation, and advancing towards a proactive security model.'
        : 'The organisation demonstrates strong cybersecurity maturity. Continued investment in threat intelligence, advanced detection capabilities, and security culture will maintain this position.',
    };
  }, [scores, profile]);

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="report-tab">
      <div className="bg-white border border-gray-200 rounded-xl p-8">
        <div className="border-b border-gray-200 pb-6 mb-6">
          <div className="flex items-center gap-3 mb-3">
            <ShieldCheck className="w-8 h-8 text-emerald-600" />
            <h2 className="text-xl font-bold text-gray-900">{report.title}</h2>
          </div>
          {profile.company_name && <p className="text-base font-medium text-gray-700">{profile.company_name}</p>}
          <p className="text-sm text-gray-500">Generated {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
        </div>

        <section className="mb-6">
          <h3 className="text-base font-semibold text-gray-800 mb-2 flex items-center gap-2"><FileText className="w-4 h-4 text-emerald-600" /> Executive Summary</h3>
          <p className="text-sm text-gray-700 leading-relaxed">{report.summary}</p>
        </section>
        <section className="mb-6">
          <h3 className="text-base font-semibold text-gray-800 mb-2 flex items-center gap-2"><Target className="w-4 h-4 text-emerald-600" /> Overall Assessment</h3>
          <p className="text-sm text-gray-700 leading-relaxed">{report.overallScore}</p>
        </section>
        <section className="mb-6">
          <h3 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2"><Shield className="w-4 h-4 text-emerald-600" /> Domain Analysis</h3>
          <div className="space-y-2">
            {report.domainAnalysis.map((d, i) => (
              <div key={i} className="flex items-start gap-3 text-sm">
                <div className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: d.color }} />
                <p className="text-gray-700">{d.analysis}</p>
              </div>
            ))}
          </div>
        </section>
        <section className="mb-6">
          <h3 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2"><AlertCircle className="w-4 h-4 text-emerald-600" /> Key Findings</h3>
          <ul className="space-y-1.5">
            {report.keyFindings.map((f, i) => (
              <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                <span className="text-emerald-500 mt-0.5">-</span> {f}
              </li>
            ))}
          </ul>
        </section>
        <section className="bg-emerald-50 border border-emerald-200 rounded-lg p-5">
          <h3 className="text-base font-semibold text-emerald-800 mb-2 flex items-center gap-2"><Info className="w-4 h-4" /> Strategic Recommendation</h3>
          <p className="text-sm text-emerald-700 leading-relaxed">{report.recommendation}</p>
        </section>
      </div>
    </div>
  );
};

// ─── Main Page ───
const CyberSecurityPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const assessmentIdFromUrl = searchParams.get('id');

  // Phase: 'intro' -> 'profile' -> 'assessment' -> 'submitting' -> 'sample' -> 'verify' -> 'results'
  const [phase, setPhase] = useState(assessmentIdFromUrl ? 'loading' : 'intro');
  const [domainStep, setDomainStep] = useState(0);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [secretCode, setSecretCode] = useState('');
  const [assessmentId, setAssessmentId] = useState(assessmentIdFromUrl || '');
  const [sampleResults, setSampleResults] = useState(null);
  const [fullResults, setFullResults] = useState(null);
  const [downloadingPptx, setDownloadingPptx] = useState(false);

  const [profile, setProfile] = useState({
    company_name: '', company_url: '', industry: '', org_size: '',
    annual_revenue: '', respondent_name: '', respondent_role: '', respondent_email: '',
  });

  const [scores, setScores] = useState(() => {
    const init = {};
    DOMAINS.forEach(d => d.controls.forEach(c => { init[c.id] = 2; }));
    return init;
  });

  // Load existing assessment from URL
  useEffect(() => {
    if (assessmentIdFromUrl) {
      loadAssessment(assessmentIdFromUrl);
    }
  }, [assessmentIdFromUrl]);

  const loadAssessment = async (id) => {
    try {
      const res = await axios.get(`${API}/api/cyber-security/assessment/${id}`);
      if (res.data.is_sample_preview) {
        setSampleResults(res.data);
        setAssessmentId(id);
        setPhase('sample');
      } else {
        setFullResults(res.data);
        setScores(res.data.scores || {});
        setProfile({
          company_name: res.data.company_name || '',
          industry: res.data.industry || '',
          org_size: res.data.org_size || '',
          company_url: res.data.company_url || '',
          annual_revenue: res.data.annual_revenue || '',
          respondent_name: res.data.respondent_name || '',
          respondent_role: res.data.respondent_role || '',
          respondent_email: res.data.respondent_email || '',
        });
        setAssessmentId(id);
        setPhase('results');
      }
    } catch (err) {
      console.error('Failed to load assessment:', err);
      setPhase('intro');
    }
  };

  const overall = getOverallMaturity(fullResults?.scores || scores);
  const totalSteps = DOMAINS.length + 1;

  const handleProfileChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateProfile = () => {
    const required = ['company_name', 'industry', 'org_size', 'respondent_name', 'respondent_email'];
    for (const f of required) {
      if (!profile[f]) { setError('Please fill in all required fields'); return false; }
    }
    if (!profile.respondent_email.includes('@')) { setError('Please enter a valid email address'); return false; }
    return true;
  };

  const handleProfileNext = () => {
    if (validateProfile()) { setError(''); setPhase('assessment'); setDomainStep(0); }
  };

  const handleDomainNext = () => {
    if (domainStep < DOMAINS.length - 1) {
      setDomainStep(domainStep + 1);
    } else {
      handleSubmitAssessment();
    }
  };

  const handleDomainBack = () => {
    if (domainStep > 0) setDomainStep(domainStep - 1);
    else setPhase('profile');
  };

  const handleSubmitAssessment = async () => {
    setSubmitting(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('company_name', profile.company_name);
      formData.append('company_url', profile.company_url);
      formData.append('industry', profile.industry);
      formData.append('org_size', profile.org_size);
      formData.append('annual_revenue', profile.annual_revenue);
      formData.append('respondent_name', profile.respondent_name);
      formData.append('respondent_role', profile.respondent_role);
      formData.append('respondent_email', profile.respondent_email);
      formData.append('scores', JSON.stringify(scores));

      const res = await submitMaturity(`${API}/api/cyber-security/assess`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });

      if (res.data.success) {
        setAssessmentId(res.data.assessment_id);
        setSampleResults(res.data.sample_results);
        setPhase('sample');
      }
    } catch (err) {
      console.error('Submit error:', err);
      setError(err.response?.data?.detail || 'Failed to submit assessment. Please try again.');
      setPhase('assessment');
      setDomainStep(DOMAINS.length - 1);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyCode = async () => {
    setVerifying(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('assessment_id', assessmentId);
      formData.append('secret_code', secretCode);

      const res = await axios.post(`${API}/api/cyber-security/verify-code`, formData, { timeout: 30000 });

      if (res.data.success && res.data.full_results) {
        setFullResults(res.data.full_results);
        setScores(res.data.full_results.scores || scores);
        setProfile({
          company_name: res.data.full_results.company_name || profile.company_name,
          industry: res.data.full_results.industry || profile.industry,
          org_size: res.data.full_results.org_size || profile.org_size,
          company_url: res.data.full_results.company_url || '',
          annual_revenue: res.data.full_results.annual_revenue || '',
          respondent_name: res.data.full_results.respondent_name || '',
          respondent_role: res.data.full_results.respondent_role || '',
          respondent_email: res.data.full_results.respondent_email || '',
        });
        setPhase('results');
      }
    } catch (err) {
      console.error('Verify error:', err);
      setError(err.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const handleDownloadPptx = async () => {
    setDownloadingPptx(true);
    try {
      await generateCyberSecurityPptx(fullResults || { ...profile, scores, overall_score: overall, domain_scores: {} });
    } catch (err) {
      console.error('PPTX error:', err);
    } finally {
      setDownloadingPptx(false);
    }
  };

  const currentStep = phase === 'profile' ? 1 : phase === 'assessment' ? domainStep + 2 : totalSteps;
  const progressPercent = (currentStep / totalSteps) * 100;

  // ─── Loading ───
  if (phase === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-emerald-600 mx-auto mb-4" />
          <p className="text-gray-500">Loading assessment...</p>
        </div>
      </div>
    );
  }

  // ─── Submitting ───
  if (submitting) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50 to-white flex items-center justify-center">
        <div className="text-center bg-white rounded-2xl shadow-lg p-12 max-w-md">
          <Loader2 className="w-14 h-14 animate-spin text-emerald-600 mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing Assessment</h2>
          <p className="text-gray-500">Saving your responses and generating your report...</p>
          <p className="text-sm text-gray-400 mt-4">A secret code will be sent to your email</p>
        </div>
      </div>
    );
  }

  // ─── Intro Page ───
  if (phase === 'intro') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50 to-white">
        <header className="bg-white/80 backdrop-blur-sm border-b border-gray-100 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-slate-800 to-emerald-700 rounded-xl flex items-center justify-center">
                <ShieldAlert className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">SAMANVAI</span>
            </Link>
            <Link to="/"><Button variant="outline" className="gap-2"><Home className="w-4 h-4" /> Home</Button></Link>
          </div>
        </header>

        <div className="max-w-5xl mx-auto px-4 py-16">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <ShieldCheck className="w-4 h-4" />
              Cybersecurity Maturity Assessment
            </div>
            <h1 data-testid="page-title" className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Assess Your Organisation's
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600"> Cyber Security Maturity</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Comprehensive assessment across 6 domains and 30 controls, aligned with NIST CSF, ISO 27001, and CIS Controls.
              Get a prioritised action plan to strengthen your defences.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
            {DOMAINS.map(d => {
              const Icon = d.icon;
              return (
                <div key={d.id} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: `${d.color}15` }}>
                    <Icon className="w-5 h-5" style={{ color: d.color }} />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">{d.name}</h3>
                  <p className="text-sm text-gray-500">{d.subtitle}</p>
                </div>
              );
            })}
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">What You'll Get</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { icon: Target, title: 'Live Maturity Dashboard', desc: 'Radar chart, domain scores, and key metrics at a glance' },
                { icon: AlertTriangle, title: 'Prioritised Gap Analysis', desc: 'Critical, high, and medium findings ranked by severity' },
                { icon: CheckCircle2, title: 'Action Plan by Timeframe', desc: 'Immediate, 30-day, 90-day, and ongoing recommendations' },
                { icon: FileText, title: 'Executive Report', desc: 'Board-ready summary narrative with domain analysis' },
              ].map((item, idx) => (
                <div key={idx} className="flex gap-4">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{item.title}</h3>
                    <p className="text-sm text-gray-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center">
            <Button
              onClick={() => setPhase('profile')}
              size="lg"
              className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white px-12 py-6 text-lg gap-3"
              data-testid="start-assessment-btn"
            >
              Start Assessment <ArrowRight className="w-5 h-5" />
            </Button>
            <p className="text-sm text-gray-500 mt-4">Takes approximately 10-15 minutes to complete</p>
          </div>
        </div>
      </div>
    );
  }

  // ─── Profile Step ───
  if (phase === 'profile') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50 to-white py-8">
        <div className="max-w-2xl mx-auto px-4">
          <div className="mb-8">
            <Progress value={progressPercent} className="h-2" />
            <p className="text-sm text-gray-500 mt-2 text-center">Step 1 of {totalSteps} - Organisation Profile</p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Organisation Profile</h2>
              <p className="text-gray-500">Tell us about your organisation to personalise results</p>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company Name *</label>
                <Input value={profile.company_name} onChange={e => handleProfileChange('company_name', e.target.value)} placeholder="Enter company name" data-testid="input-company" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company Website <span className="text-xs text-gray-400">(Optional)</span></label>
                <Input value={profile.company_url} onChange={e => handleProfileChange('company_url', e.target.value)} placeholder="https://company.com" data-testid="input-url" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Industry *</label>
                <Select value={profile.industry} onValueChange={v => handleProfileChange('industry', v)}>
                  <SelectTrigger data-testid="select-industry"><SelectValue placeholder="Select industry" /></SelectTrigger>
                  <SelectContent>{SECTORS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Organisation Size *</label>
                  <Select value={profile.org_size} onValueChange={v => handleProfileChange('org_size', v)}>
                    <SelectTrigger data-testid="select-orgsize"><SelectValue placeholder="Select size" /></SelectTrigger>
                    <SelectContent>{ORG_SIZES.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Annual Revenue <span className="text-xs text-gray-400">(Optional)</span></label>
                  <Select value={profile.annual_revenue} onValueChange={v => handleProfileChange('annual_revenue', v)}>
                    <SelectTrigger><SelectValue placeholder="Select revenue" /></SelectTrigger>
                    <SelectContent>{REVENUE_RANGES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div className="pt-4 border-t">
                <h3 className="font-medium text-gray-900 mb-4">Your Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Your Name *</label>
                    <Input value={profile.respondent_name} onChange={e => handleProfileChange('respondent_name', e.target.value)} placeholder="Full name" data-testid="input-name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Your Role <span className="text-xs text-gray-400">(Optional)</span></label>
                    <Input value={profile.respondent_role} onChange={e => handleProfileChange('respondent_role', e.target.value)} placeholder="e.g. CISO, IT Director" />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <Input type="email" value={profile.respondent_email} onChange={e => handleProfileChange('respondent_email', e.target.value)} placeholder="work@company.com" data-testid="input-email" />
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-600 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" /> {error}
                </div>
              )}

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setPhase('intro')} className="gap-2"><ChevronLeft className="w-4 h-4" /> Back</Button>
                <Button onClick={handleProfileNext} className="bg-emerald-600 hover:bg-emerald-700 gap-2">Continue <ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Assessment Steps (one domain per step) ───
  if (phase === 'assessment') {
    const domain = DOMAINS[domainStep];
    const isLast = domainStep === DOMAINS.length - 1;

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50 to-white py-8">
        <div className="max-w-3xl mx-auto px-4">
          <div className="mb-8">
            <Progress value={progressPercent} className="h-2" />
            <p className="text-sm text-gray-500 mt-2 text-center">
              Step {domainStep + 2} of {totalSteps} - {domain.name}
            </p>
          </div>

          <DomainStep domain={domain} scores={scores} setScores={setScores} />

          <div className="flex justify-between mt-6">
            <Button variant="outline" onClick={handleDomainBack} className="gap-2">
              <ChevronLeft className="w-4 h-4" /> Back
            </Button>
            {isLast ? (
              <Button
                onClick={handleDomainNext}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 gap-2"
                data-testid="btn-complete"
                disabled={submitting}
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {submitting ? 'Submitting...' : 'Complete Assessment'}
              </Button>
            ) : (
              <Button onClick={handleDomainNext} className="bg-emerald-600 hover:bg-emerald-700 gap-2">
                Continue <ChevronRight className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─── Sample Results + Verification ───
  if (phase === 'sample') {
    const sr = sampleResults || {};
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50 to-white py-8">
        <div className="max-w-3xl mx-auto px-4">
          {/* Sample Preview */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8">
            <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-emerald-900 p-8 text-white">
              <div className="flex items-center gap-3 mb-2">
                <ShieldCheck className="w-8 h-8 text-emerald-400" />
                <h2 className="text-2xl font-bold">Assessment Complete</h2>
              </div>
              <p className="text-gray-300">{sr.company_name} {sr.industry ? `| ${sr.industry}` : ''}</p>
            </div>

            <div className="p-8">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-3xl font-bold" style={{ color: getScoreColor(sr.overall_score || 0) }}>
                    {sr.overall_score || 0}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Overall Score</div>
                  <Badge className="text-white text-xs mt-2" style={{ backgroundColor: getScoreColor(sr.overall_score || 0) }}>
                    {sr.maturity_level || 'N/A'}
                  </Badge>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-xl font-bold text-green-600">{sr.strongest_score || '-'}</div>
                  <div className="text-xs text-gray-500 mt-1">Strongest</div>
                  <div className="text-xs text-green-600 font-medium mt-1 truncate">{sr.strongest_domain}</div>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <div className="text-xl font-bold text-red-600">{sr.weakest_score || '-'}</div>
                  <div className="text-xs text-gray-500 mt-1">Weakest</div>
                  <div className="text-xs text-red-600 font-medium mt-1 truncate">{sr.weakest_domain}</div>
                </div>
                <div className="text-center p-4 bg-amber-50 rounded-lg">
                  <div className="text-xl font-bold text-amber-600">{sr.total_findings || 0}</div>
                  <div className="text-xs text-gray-500 mt-1">Findings</div>
                  <div className="text-xs text-gray-500 mt-1">{sr.critical_gaps || 0} critical, {sr.high_gaps || 0} high</div>
                </div>
              </div>

              {/* Preview findings */}
              {sr.findings_preview && sr.findings_preview.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Top Findings Preview</h3>
                  {sr.findings_preview.map((f, i) => (
                    <div key={i} className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0">
                      <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: f.priority === 'Critical' ? '#ef4444' : f.priority === 'High' ? '#f97316' : '#eab308' }} />
                      <span className="text-sm text-gray-600 flex-1 truncate">{f.control}</span>
                      <Badge variant="outline" className="text-xs">{f.priority}</Badge>
                    </div>
                  ))}
                </div>
              )}

              {/* Blurred overlay hint */}
              <div className="relative bg-gray-100 rounded-lg p-6 text-center overflow-hidden">
                <div className="absolute inset-0 bg-gray-100 backdrop-blur-sm flex items-center justify-center z-10">
                  <div className="text-center">
                    <EyeOff className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-600">Full report locked</p>
                    <p className="text-xs text-gray-400">Enter your secret code below to unlock</p>
                  </div>
                </div>
                <div className="opacity-20 blur-sm">
                  <p>Radar charts, domain analysis, recommendations...</p>
                </div>
              </div>
            </div>
          </div>

          {/* Secret Code Entry */}
          <div className="bg-white rounded-2xl shadow-lg p-8" data-testid="verify-section">
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <KeyRound className="w-7 h-7 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Enter Your Secret Code</h3>
              <p className="text-sm text-gray-500 mt-1">
                A secret access code has been sent to <strong>{profile.respondent_email || sr.respondent_email || 'your email'}</strong>
              </p>
            </div>

            <div className="max-w-sm mx-auto space-y-4">
              <Input
                value={secretCode}
                onChange={e => { setSecretCode(e.target.value); setError(''); }}
                placeholder="Enter 6-digit code"
                className="text-center text-2xl tracking-[0.3em] font-mono h-14"
                maxLength={10}
                data-testid="input-secret-code"
              />

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-600 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" /> {error}
                </div>
              )}

              <Button
                onClick={handleVerifyCode}
                disabled={verifying || !secretCode}
                className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 gap-2"
                data-testid="btn-verify"
              >
                {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                {verifying ? 'Verifying...' : 'Unlock Full Report'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Full Results Phase ───
  const resultScores = fullResults?.scores || scores;
  const resultProfile = fullResults ? {
    company_name: fullResults.company_name, industry: fullResults.industry, org_size: fullResults.org_size,
  } : profile;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-emerald-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <ShieldAlert className="w-8 h-8 text-emerald-400" />
                <h1 className="text-2xl sm:text-3xl font-bold">Cyber Security Assessment</h1>
              </div>
              {resultProfile.company_name && (
                <p className="text-sm text-gray-300 mt-1">{resultProfile.company_name} {resultProfile.industry ? `| ${resultProfile.industry}` : ''}</p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 gap-2"
                data-testid="btn-home"
              >
                <Home className="w-4 h-4" /> Home
              </Button>
              <Button
                onClick={handleDownloadPptx}
                disabled={downloadingPptx}
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 gap-2"
                data-testid="btn-download-pptx"
              >
                {downloadingPptx ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Download PPTX
              </Button>
            </div>
          </div>

          <div className="mt-6 flex items-center gap-6 flex-wrap">
            <div className="flex items-center gap-3 bg-white/10 rounded-lg px-5 py-3">
              <span className="text-sm text-gray-300">Overall Maturity</span>
              <span className="text-2xl font-bold" style={{ color: getScoreColor(overall) }}>{overall}</span>
              <span className="text-gray-400">/ 5</span>
              <Badge className="text-white ml-1" style={{ backgroundColor: getScoreColor(overall) }}>{getMaturityLabel(overall)}</Badge>
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-300">
              {DOMAINS.map(d => (
                <span key={d.id} className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: d.color }} />
                  <span className="hidden sm:inline">{d.name.split(' ')[0]}:</span>
                  <span className="font-medium text-white">{getDomainScore(d, resultScores)}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-gray-200 rounded-lg p-1 mb-6 flex-wrap h-auto" data-testid="tab-list">
            <TabsTrigger value="dashboard" data-testid="tab-dashboard" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
              <Target className="w-4 h-4 mr-1.5" /> Dashboard
            </TabsTrigger>
            <TabsTrigger value="findings" data-testid="tab-findings" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
              <AlertTriangle className="w-4 h-4 mr-1.5" /> Findings
            </TabsTrigger>
            <TabsTrigger value="recommendations" data-testid="tab-recommendations" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> Recommendations
            </TabsTrigger>
            <TabsTrigger value="report" data-testid="tab-report" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
              <FileText className="w-4 h-4 mr-1.5" /> Report
            </TabsTrigger>
            <TabsTrigger value="agi" data-testid="tab-agi" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
              <Brain className="w-4 h-4 mr-1.5" /> AGI Insights
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard"><DashboardTab scores={resultScores} profile={resultProfile} /></TabsContent>
          <TabsContent value="findings"><FindingsTab scores={resultScores} /></TabsContent>
          <TabsContent value="recommendations"><RecommendationsTab scores={resultScores} /></TabsContent>
          <TabsContent value="report"><ReportTab scores={resultScores} profile={resultProfile} /></TabsContent>
          <TabsContent value="agi">
            <AGIInsightsTab
              assessmentType="Cyber Security"
              assessmentId={assessmentId}
            />
          </TabsContent>
        </Tabs>
        <ReportSecurityNote />
      </div>
    </div>
  );
};

export default CyberSecurityPage;
